import "./native-save-destination.css";

interface SaveHandle {
  name: string;
  createWritable(): Promise<{
    write(blob: Blob): Promise<void>;
    close(): Promise<void>;
    abort(): Promise<void>;
  }>;
}
interface PickerWindow extends Window {
  showSaveFilePicker?: (options: {
    id: string;
    suggestedName: string;
    excludeAcceptAllOption: boolean;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<SaveHandle>;
}
export interface SaveDestination {
  name: string;
  write(blob: Blob): Promise<void>;
}
let choosing = false;

export function fileNameWithExtension(name: string, extension: string) {
  const base = name
    .trim()
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/\p{Cc}/gu, "_")
    .replace(/\.(3dcd|cepro|png|jpe?g)$/i, "")
    .replace(/[. ]+$/, "");
  return (base || "Мой проект") + extension;
}

function askFileName(name: string, extension: string): Promise<string | null> {
  const dialog = document.createElement("dialog");
  dialog.id = "native-file-save";
  dialog.setAttribute("aria-labelledby", "native-file-save-title");
  dialog.innerHTML = `<form method="dialog">
    <h2 id="native-file-save-title">Сохранить файл</h2>
    <label for="native-file-name">Имя файла</label>
    <input id="native-file-name" name="filename" type="text" required maxlength="180" autocomplete="off">
    <p>Этот браузер не поддерживает выбор папки сайтом. Файл будет сохранён по настройкам загрузок браузера. Чтобы выбирать папку каждый раз, включите в них запрос места сохранения.</p>
    <footer><button type="button" data-save-cancel>Отмена</button><button type="submit" class="primary">Сохранить</button></footer>
  </form>`;
  const input = dialog.querySelector<HTMLInputElement>("input")!;
  input.value = fileNameWithExtension(name, extension);
  document.getElementById("native-editor")!.append(dialog);
  return new Promise((resolve) => {
    let result: string | null = null;
    dialog.querySelector("form")!.onsubmit = () => {
      result = fileNameWithExtension(input.value, extension);
    };
    dialog.querySelector<HTMLButtonElement>("[data-save-cancel]")!.onclick =
      () => dialog.close();
    dialog.addEventListener(
      "close",
      () => {
        dialog.remove();
        resolve(result);
      },
      { once: true },
    );
    dialog.showModal();
    input.focus();
    input.setSelectionRange(0, input.value.length - extension.length);
  });
}

/** Call directly from the user gesture, before compression or image rendering. */
export async function chooseSaveDestination(
  name: string,
  extension: ".3dcd" | ".png" | ".jpg",
  mime: string,
): Promise<SaveDestination | null> {
  if (choosing) return null;
  choosing = true;
  try {
    const picker = (window as PickerWindow).showSaveFilePicker;
    if (picker) {
      let handle: SaveHandle;
      try {
        handle = await picker.call(window, {
          id: extension === ".3dcd" ? "creator-project" : "creator-image",
          suggestedName: fileNameWithExtension(name, extension),
          excludeAcceptAllOption: true,
          types: [
            {
              description:
                extension === ".3dcd"
                  ? "Проект 3D Cover Design"
                  : "Изображение",
              accept: { [mime]: [extension] },
            },
          ],
        });
      } catch (error) {
        if ((error as Error).name === "AbortError") return null;
        throw error;
      }
      return {
        name: handle.name,
        async write(blob) {
          const stream = await handle.createWritable();
          try {
            await stream.write(blob);
            await stream.close();
          } catch (error) {
            try {
              await stream.abort();
            } catch {
              /* Preserve original write error. */
            }
            throw error;
          }
        },
      };
    }
    const chosen = await askFileName(name, extension);
    if (chosen === null) return null;
    return {
      name: chosen,
      async write(blob) {
        const url = URL.createObjectURL(blob),
          link = document.createElement("a");
        link.href = url;
        link.download = chosen;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      },
    };
  } finally {
    choosing = false;
  }
}
